import { useEffect } from 'react';

import type { VenueDetail } from '../../../api/venues';
import { CreateDrawer, type CreateDrawerStep } from '../primitives';
import { useWritableRegions } from '../primitives/use-writable-regions';

import { IdentityStep } from './IdentityStep';
import { LocationStep } from './LocationStep';
import { ReviewStep } from './ReviewStep';
import { useVenueCreateForm } from './useVenueCreateForm';

interface VenueCreateDrawerProps {
  /** Region label shown in the header eyebrow (e.g. the RD's region name). */
  regionLabel?: string;
  onClose: () => void;
  onCreated: (venue: VenueDetail) => void;
}

/**
 * VenueCreateDrawer (BON-158) — guided venue create over the Venues surface.
 * Built on the DS CreateDrawer primitive; all state/logic lives in
 * useVenueCreateForm and the step bodies in venueCreateSteps.
 */
export default function VenueCreateDrawer({
  regionLabel,
  onClose,
  onCreated,
}: VenueCreateDrawerProps) {
  const writable = useWritableRegions();
  const form = useVenueCreateForm(onCreated, writable.regions);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const steps: CreateDrawerStep[] = [
    {
      id: 'identity',
      label: 'Identity',
      hint: 'status + name + domain',
      content: <IdentityStep form={form} />,
    },
    {
      id: 'location',
      label: 'Location',
      hint: 'address + region',
      content: <LocationStep form={form} writable={writable} />,
    },
    {
      id: 'review',
      label: 'Review',
      hint: 'confirm + create',
      content: <ReviewStep form={form} regions={writable.regions} />,
    },
  ];

  const ctaLabel = form.submitting
    ? 'Creating…'
    : form.stage === 'prospect'
      ? 'Create prospect'
      : 'Create venue';

  return (
    <div
      className="nb-dashboards nb-cdrawer-scrim"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        justifyContent: 'flex-end',
        background: 'rgba(15, 25, 18, 0.35)',
      }}
      onClick={onClose}
      role="presentation"
    >
      <div style={{ height: '100%' }} onClick={(e) => e.stopPropagation()}>
        <CreateDrawer
          title="Add venue"
          eyebrow={regionLabel ? `New venue · ${regionLabel}` : 'New venue'}
          quote="Great workshops grow in borrowed rooms."
          attribution="Bonsai Bar · pop-up field notes"
          steps={steps}
          step={form.step}
          onStepChange={form.setStep}
          ctaLabel={ctaLabel}
          onCta={form.submit}
          onCancel={onClose}
          onClose={onClose}
          footNote="creates in Nebari only — link FareHarbor from the venue page"
          railNote="✦ marks fields suggested from the venue's site."
          style={{ height: '100%' }}
        />
      </div>
    </div>
  );
}
